import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment-timezone';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useActivationDevices from '@/src/Activations/components/hooks/useActivationDevices';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useZipPhotos from '@/src/Activations/components/hooks/useZipPhotos';
import useShare from '@/src/Share/components/hooks/useShare';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useMediaTags from '@/src/Activations/components/hooks/useMediaTags';
import { EditBatch } from '@/src/Common/components/hooks/useMediaEdit';
import BulkUploadMedia from '@/src/Common/components/BulkUploadMedia';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { Crumb, Toggle } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Chip, Chipbar, MenuItem, MenuLabel, MenuSep, Segmented, TCard, TEmpty } from '../../components/table';
import {
    ConfirmHost,
    ConfirmSpec,
    errText,
    IconCheck,
    IconDownload,
    IconEye,
    IconEyeOff,
    IconFilter,
    IconHeart,
    IconHistory,
    IconPlay,
    IconSliders,
    IconUpload,
    isVideo,
    matches,
    plural,
    scopeNote,
    scopeOf,
    TypeKey,
    TYPES,
    useDismiss,
} from './media/parts';
import Lightbox from './media/Lightbox';
import { CopySheet, ShareSheet, TagSheet, ZipRequests, ZipSheet } from './media/ActionDialogs';
import { BulkEditSheet, EditHistorySheet, EditProgressSheet, VersionsSheet } from './media/EditDialogs';
import '../../pages-event.css';
import './media.css';

/*
 * Media — every file the event captured: type chips with counts,
 * source/group/tag filters, view options, selection with bulk
 * hide/show, tag, share, copy, zip and edit, a lightbox with tags,
 * replace and version history, and new-photo polling. Same requests
 * as v2; every write and send is confirmed and reports failure.
 */

const PAGE = 60;
const AUTO_KEY = 'psv2-media-auto';
const ALL = '__all';

type SheetKey = 'upload' | 'zip' | 'share' | 'copy' | 'tag' | 'edit' | 'history' | null;

const readAuto = () => {
    try {
        return window.localStorage.getItem(AUTO_KEY) === '1';
    } catch {
        return false;
    }
};

const maxIdOf = (rows: any[]) => rows.reduce((m: number, r: any) => Math.max(m, r.id ?? 0), 0);

function MediaBody() {
    const navigate = useNavigate();
    const { activationId: id, galleryId, gallerySlug, domain: galleryUrl, invalidateMedia } = useEventContext();
    const { getActivationPhotos } = useActivation();
    const { devices } = useActivationDevices(id);
    const authenticatedApi = useAuthenticatedApi();
    const { createZip, getZipRequestsForActivation } = useZipPhotos();
    const { createShare } = useShare();
    const { getActivations } = useActivations();
    const { addTagsByShareIdentifiers, removeTagsByShareIdentifiers } = useMediaTags();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useToast();

    const [media, setMedia] = useState<any[] | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [type, setType] = useState<TypeKey>('all');
    const [source, setSource] = useState<string | null>(null);
    const [group, setGroup] = useState<string | null>(null);
    const [tag, setTag] = useState<string | null>(null);
    const [sortDesc, setSortDesc] = useState(true);
    const [cols, setCols] = useState(6);
    const [showHidden, setShowHidden] = useState(false);
    const [auto, setAuto] = useState(readAuto);
    const [limit, setLimit] = useState(PAGE);
    const [selected, setSelected] = useState<Set<number>>(() => new Set());
    const [anchor, setAnchor] = useState<number | null>(null);
    const [newCount, setNewCount] = useState(0);
    const [popover, setPopover] = useState<'filter' | 'view' | null>(null);
    const [zipRequests, setZipRequests] = useState<any[]>([]);
    const [zipListOpen, setZipListOpen] = useState(false);
    const [faces, setFaces] = useState<any[]>([]);
    const [facesExpanded, setFacesExpanded] = useState(false);
    const [sheet, setSheet] = useState<SheetKey>(null);
    const [zipOf, setZipOf] = useState<'selection' | 'filtered'>('filtered');
    const [copyTargets, setCopyTargets] = useState<any[] | null>(null);
    const [copyFailed, setCopyFailed] = useState(false);
    const [lb, setLb] = useState<{ ids: number[]; id: number } | null>(null);
    const [activeBatch, setActiveBatch] = useState<EditBatch | null>(null);
    const [versionsOf, setVersionsOf] = useState<any | null>(null);
    const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
    const [rowBusy, setRowBusy] = useState<Set<number>>(() => new Set());

    const maxId = useRef(0);
    const busyIds = useRef(new Set<number>());
    const mounted = useRef(true);
    const deepLinked = useRef(false);
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );

    const closePopover = useCallback(() => setPopover(null), []);
    const filterRef = useDismiss(popover === 'filter', closePopover);
    const viewRef = useDismiss(popover === 'view', closePopover);

    /* ---- loading ---- */

    const load = useCallback(
        async (silent = false) => {
            if (!silent) setLoadFailed(false);
            try {
                const rows = await getActivationPhotos(id, []);
                if (!mounted.current) return;
                const list = Array.isArray(rows) ? rows : [];
                setMedia(list);
                maxId.current = maxIdOf(list);
                setNewCount(0);
            } catch {
                if (!mounted.current) return;
                /* keep what's on screen when a background refresh fails */
                if (silent) toast.show('Couldn’t refresh the grid — showing what was loaded.');
                else setLoadFailed(true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [id],
    );

    /* after any write: re-read the grid and drop the event-wide cache */
    const refresh = useCallback(() => {
        invalidateMedia();
        return load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    const loadZips = useCallback(() => {
        getZipRequestsForActivation(id)
            .then((r) => mounted.current && setZipRequests(Array.isArray(r?.data) ? r.data : []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        load();
        loadZips();
        authenticatedApi
            .get(`/public/media/unique-faces/${id}`)
            .then((r) => mounted.current && setFaces(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /* poll: auto-refresh swaps silently, otherwise show the pulse */
    useEffect(() => {
        const timer = setInterval(async () => {
            const rows = (await getActivationPhotos(id, []).catch(() => null)) ?? null;
            if (!rows || !mounted.current) return;
            const fresh = rows.filter((r: any) => r.id > maxId.current).length;
            if (auto && fresh > 0) {
                setMedia(rows);
                maxId.current = maxIdOf(rows);
            } else if (fresh > 0) {
                setNewCount(fresh);
            }
        }, 10 * 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, auto]);

    /* ---- derived ---- */

    const all = useMemo(() => media ?? [], [media]);
    const byId = useMemo(() => new Map(all.map((m) => [m.id, m])), [all]);

    const deviceName = useCallback(
        (key: string | null) => {
            if (!key) return 'Uploaded';
            const d = devices.find((x) => x.deviceKeyId === key);
            return d?.displayName ?? d?.deviceName ?? key;
        },
        [devices],
    );

    const sources = useMemo(() => {
        const c: Record<string, number> = {};
        all.forEach((m) => {
            const k = m.deviceKeyId ?? '';
            c[k] = (c[k] ?? 0) + 1;
        });
        return Object.entries(c).map(([k, n]) => ({ key: k, label: deviceName(k || null), count: n }));
    }, [all, deviceName]);

    const groupKeys = useMemo(() => {
        const c: Record<string, number> = {};
        all.forEach((m) => {
            if (m.groupKey) c[m.groupKey] = (c[m.groupKey] ?? 0) + 1;
        });
        return Object.entries(c);
    }, [all]);

    const tagCounts = useMemo(() => {
        const c: Record<string, number> = {};
        all.forEach((m) =>
            (m.tags ?? []).forEach((t: string) => {
                c[t] = (c[t] ?? 0) + 1;
            }),
        );
        return Object.entries(c).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    }, [all]);

    const filtered = useMemo(() => {
        let rows = all.filter((m) => matches(m, type));
        if (!showHidden) rows = rows.filter((m) => !m.isHidden);
        if (source != null) rows = rows.filter((m) => (m.deviceKeyId ?? '') === source);
        if (group != null) rows = rows.filter((m) => (m.groupKey ?? '') === group);
        if (tag != null) rows = rows.filter((m) => (m.tags ?? []).includes(tag));
        return [...rows].sort((a, b) => {
            const av = moment(a.capturedAt ?? a.createdAt ?? 0).valueOf();
            const bv = moment(b.capturedAt ?? b.createdAt ?? 0).valueOf();
            return sortDesc ? bv - av : av - bv;
        });
    }, [all, type, showHidden, source, group, tag, sortDesc]);

    const counts = useMemo(() => {
        const c: Record<string, number> = {};
        TYPES.forEach((t) => {
            c[t.key] = all.filter((m) => matches(m, t.key) && (showHidden || !m.isHidden)).length;
        });
        return c;
    }, [all, showHidden]);

    const visible = filtered.slice(0, limit);
    const hiddenCount = all.filter((m) => m.isHidden).length;
    const selectedRows = useMemo(() => all.filter((m) => selected.has(m.id)), [all, selected]);
    const anySelectedHidden = selectedRows.some((m) => m.isHidden);
    const selectionTags = useMemo(() => {
        const s = new Set<string>();
        selectedRows.forEach((m) => (m.tags ?? []).forEach((t: string) => s.add(t)));
        return Array.from(s).sort();
    }, [selectedRows]);

    /* drop selections that no longer exist after a reload */
    useEffect(() => {
        if (!media) return;
        setSelected((s) => {
            const keep = Array.from(s).filter((x) => byId.has(x));
            return keep.length === s.size ? s : new Set(keep);
        });
    }, [media, byId]);

    const resetPage = () => setLimit(PAGE);
    const pick = (apply: () => void) => () => {
        apply();
        resetPage();
        closePopover();
    };
    const filterCount = (source != null ? 1 : 0) + (group != null ? 1 : 0) + (tag != null ? 1 : 0);
    const chips: Chip[] = [
        ...(source != null ? [{ label: 'Source', value: deviceName(source || null), onClear: () => setSource(null) }] : []),
        ...(group != null ? [{ label: 'Group', value: group, onClear: () => setGroup(null) }] : []),
        ...(tag != null ? [{ label: 'Tag', value: tag, onClear: () => setTag(null) }] : []),
    ];

    /* ---- lightbox + ?sid= deep link ---- */

    const setSid = (sid: string | null) => {
        const p = new URLSearchParams(searchParams);
        if (sid) p.set('sid', sid);
        else p.delete('sid');
        setSearchParams(p, { replace: true });
    };

    const openLightbox = (m: any) => {
        setLb({ ids: filtered.map((x) => x.id), id: m.id });
        if (m.shareIdentifier) setSid(m.shareIdentifier);
    };
    const stepLightbox = (next: number) => {
        setLb((l) => (l ? { ...l, id: next } : l));
        const m = byId.get(next);
        if (m?.shareIdentifier) setSid(m.shareIdentifier);
    };
    const closeLightbox = () => {
        setLb(null);
        setSid(null);
    };

    useEffect(() => {
        if (deepLinked.current || media == null) return;
        deepLinked.current = true;
        const sid = searchParams.get('sid');
        if (!sid) return;
        const m = filtered.find((x) => x.shareIdentifier === sid) ?? all.find((x) => x.shareIdentifier === sid);
        if (m) setLb({ ids: (filtered.includes(m) ? filtered : [m]).map((x) => x.id), id: m.id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [media]);

    /* ---- selection ---- */

    const toggleSelect = (m: any, range: boolean) => {
        setSelected((s) => {
            const next = new Set(s);
            if (range && anchor != null) {
                const a = visible.findIndex((x) => x.id === anchor);
                const b = visible.findIndex((x) => x.id === m.id);
                if (a >= 0 && b >= 0) {
                    visible.slice(Math.min(a, b), Math.max(a, b) + 1).forEach((x) => next.add(x.id));
                    return next;
                }
            }
            if (next.has(m.id)) next.delete(m.id);
            else next.add(m.id);
            return next;
        });
        setAnchor(m.id);
    };
    const clearSelection = () => {
        setSelected(new Set());
        setAnchor(null);
    };

    /* ---- per-item writes (immediate, reverted on failure) ---- */

    const setRow = (mediaId: number, patch: Record<string, unknown>) =>
        setMedia((rows) => (rows ? rows.map((r) => (r.id === mediaId ? { ...r, ...patch } : r)) : rows));

    const claim = (mediaId: number) => {
        if (busyIds.current.has(mediaId)) return false;
        busyIds.current.add(mediaId);
        setRowBusy(new Set(busyIds.current));
        return true;
    };
    const release = (mediaId: number) => {
        busyIds.current.delete(mediaId);
        if (mounted.current) setRowBusy(new Set(busyIds.current));
    };

    const toggleHidden = async (m: any) => {
        if (!claim(m.id)) return;
        const next = !m.isHidden;
        setRow(m.id, { isHidden: next });
        const ok = await authenticatedApi
            .put(`media/${m.id}/visibility`, { isHidden: next })
            .then(() => true)
            .catch(() => false);
        release(m.id);
        if (!mounted.current) return;
        if (!ok) {
            setRow(m.id, { isHidden: m.isHidden });
            toast.show(`Couldn’t ${next ? 'hide' : 'show'} that item — nothing changed.`);
            return;
        }
        invalidateMedia();
        toast.show(next ? 'Hidden from the gallery.' : 'Showing in the gallery again.', () => toggleHidden({ ...m, isHidden: next }));
    };

    const toggleFavorite = async (m: any) => {
        if (!claim(m.id)) return;
        const next = !m.isFavorite;
        setRow(m.id, { isFavorite: next });
        const ok = await authenticatedApi
            .put(`media/${m.id}/favorite`, { isFavorite: next })
            .then(() => true)
            .catch(() => false);
        release(m.id);
        if (!mounted.current) return;
        if (!ok) {
            setRow(m.id, { isFavorite: m.isFavorite });
            toast.show(`Couldn’t ${next ? 'favorite' : 'unfavorite'} that item — nothing changed.`);
        }
    };

    const replaceFile = (m: any, file: File) =>
        setConfirm({
            title: 'Replace this file?',
            body: (
                <>
                    <p>
                        “{file.name}” ({(file.size / (1024 * 1024)).toFixed(1)} MB) replaces this {isVideo(m) ? 'item' : 'photo'} everywhere — the gallery, slideshow and
                        every link already shared.
                    </p>
                    <p>The current file is kept in version history, so you can restore it from History.</p>
                </>
            ),
            actionLabel: 'Replace file',
            busyLabel: 'Replacing…',
            danger: true,
            run: async () => {
                if (!claim(m.id)) return 'This item is still saving — try again in a moment.';
                const fd = new FormData();
                fd.append('photo', file);
                try {
                    await authenticatedApi.put(`/media/replace/${m.id}`, fd);
                } catch (e) {
                    return errText(e, 'Couldn’t replace the file — the original is unchanged. Try again.');
                } finally {
                    release(m.id);
                }
                toast.show('File replaced.');
                refresh();
            },
        });

    /* ---- bulk writes and sends (confirmed) ---- */

    const bulkHide = (isHidden: boolean) => {
        const sc = scopeOf(selectedRows, all);
        const note = scopeNote(sc);
        const verb = isHidden ? 'Hide' : 'Show';
        setConfirm({
            title: `${verb} ${plural(sc.picked, 'item')}?`,
            body: (
                <>
                    <p>{isHidden ? 'Hidden items stop showing to guests in the gallery. You can show them again any time.' : 'These items show to guests in the gallery again.'}</p>
                    {note && <p>{note}</p>}
                </>
            ),
            actionLabel: `${verb} ${plural(sc.picked, 'item')}`,
            busyLabel: isHidden ? 'Hiding…' : 'Showing…',
            run: async () => {
                if (sc.ids.length === 0) return 'None of these items have a share ID, so they can’t be changed in bulk.';
                try {
                    await authenticatedApi.put(`/media/share-identifiers/visibility`, { shareIdentifiers: sc.ids, isHidden });
                } catch (e) {
                    return errText(e, `Couldn’t ${verb.toLowerCase()} them — nothing changed. Try again.`);
                }
                const set = new Set(sc.ids);
                setMedia((rows) => (rows ? rows.map((r) => (r.shareIdentifier && set.has(r.shareIdentifier) ? { ...r, isHidden } : r)) : rows));
                clearSelection();
                toast.show(`${isHidden ? 'Hid' : 'Showing'} ${plural(sc.files || sc.picked, 'item')}.`);
                refresh();
            },
        });
    };

    const zipScope = useMemo(() => scopeOf(zipOf === 'selection' ? selectedRows : filtered, all), [zipOf, selectedRows, filtered, all]);
    const zipLabel = zipOf === 'selection' ? `${selectedRows.length.toLocaleString()} selected` : `all ${filtered.length.toLocaleString()} shown`;

    const openZip = (of: 'selection' | 'filtered') => {
        setZipOf(of);
        setSheet('zip');
    };

    const reviewZip = (email: string) => {
        const sc = zipScope;
        const note = scopeNote(sc);
        setConfirm({
            title: 'Email a download link?',
            body: (
                <>
                    <p>
                        We’ll zip the originals for {plural(sc.picked, 'item')} and email a download link to <b>{email}</b> when it’s ready. Progress shows under Downloads on
                        this page.
                    </p>
                    {note && <p>{note}</p>}
                </>
            ),
            actionLabel: 'Create zip & email link',
            busyLabel: 'Requesting…',
            run: async () => {
                try {
                    await createZip(sc.ids, email, id);
                } catch (e) {
                    return errText(e, 'The zip request didn’t go through — nothing was sent. Try again.');
                }
                setSheet(null);
                toast.show(`Zip started — the link goes to ${email}.`);
                loadZips();
            },
        });
    };

    const downloadSelection = () => {
        if (selectedRows.length === 1) {
            window.open(selectedRows[0].originalUri, '_blank', 'noopener');
            return;
        }
        openZip('selection');
    };

    const selScope = useMemo(() => scopeOf(selectedRows, all), [selectedRows, all]);

    const reviewShare = (dest: string) => {
        const sc = selScope;
        const email = dest.includes('@');
        const note = scopeNote(sc);
        setConfirm({
            title: `Send ${plural(sc.picked, 'item')} to ${dest}?`,
            body: (
                <>
                    <p>
                        This sends {email ? 'an email' : 'a text message'} to <b>{dest}</b> right now with a link to {plural(sc.picked, 'item')}. It can’t be unsent.
                    </p>
                    {note && <p>{note}</p>}
                </>
            ),
            actionLabel: email ? 'Send email' : 'Send text',
            busyLabel: 'Sending…',
            run: async () => {
                if (galleryId == null) return 'This event has no gallery to share from.';
                const res = await createShare({
                    activationId: id,
                    galleryId,
                    shareIdentifiers: sc.ids,
                    destination: dest,
                    type: email ? 0 : 1,
                    disclaimerAccepted: true,
                }).catch(() => null);
                /* the hook swallows errors and returns an empty id */
                if (!res?.id) return `The share to ${dest} didn’t go out — check the address and try again.`;
                setSheet(null);
                clearSelection();
                toast.show(`Sent ${plural(sc.picked, 'item')} to ${dest}.`);
            },
        });
    };

    const loadTargets = () => {
        setCopyFailed(false);
        getActivations()
            .then((rows: any[]) => mounted.current && setCopyTargets((Array.isArray(rows) ? rows : []).filter((a) => a.id !== id && !a.isArchived)))
            .catch(() => mounted.current && setCopyFailed(true));
    };
    const openCopy = () => {
        setSheet('copy');
        if (copyTargets == null) loadTargets();
    };

    const reviewCopy = (target: any) => {
        const sc = selScope;
        const note = scopeNote(sc);
        setConfirm({
            title: `Copy ${plural(sc.picked, 'item')} to “${target.name}”?`,
            body: (
                <>
                    <p>Copies are added to that event. Nothing in this event changes.</p>
                    {note && <p>{note}</p>}
                </>
            ),
            actionLabel: `Copy ${plural(sc.picked, 'item')}`,
            busyLabel: 'Copying…',
            run: async () => {
                if (sc.ids.length === 0) return 'None of these items have a share ID to copy.';
                try {
                    await authenticatedApi.post('/media/copy-to-activation', { shareIdentifiers: sc.ids, targetActivationId: target.id });
                } catch (e) {
                    return errText(e, 'Copy failed — nothing was copied. Try again.');
                }
                setSheet(null);
                clearSelection();
                toast.show(`Copied ${plural(sc.picked, 'item')} to ${target.name}.`);
            },
        });
    };

    const reviewTags = (mode: 'add' | 'remove', tags: string[]) => {
        const sc = selScope;
        const note = scopeNote(sc);
        const add = mode === 'add';
        setConfirm({
            title: `${add ? 'Add' : 'Remove'} ${plural(tags.length, 'tag')} ${add ? 'to' : 'from'} ${plural(sc.picked, 'item')}?`,
            body: (
                <>
                    <p>
                        {add ? 'Adds' : 'Removes'} <b>{tags.join(', ')}</b>. Tag-filtered slideshows and gallery tag filters pick this up right away.
                    </p>
                    {note && <p>{note}</p>}
                </>
            ),
            actionLabel: `${add ? 'Add' : 'Remove'} ${plural(tags.length, 'tag')}`,
            busyLabel: add ? 'Adding…' : 'Removing…',
            danger: !add,
            run: async () => {
                try {
                    await (add ? addTagsByShareIdentifiers(sc.ids, tags) : removeTagsByShareIdentifiers(sc.ids, tags));
                } catch (e) {
                    return errText(e, `Couldn’t ${add ? 'add' : 'remove'} the tags — nothing changed. Try again.`);
                }
                setSheet(null);
                clearSelection();
                toast.show(`${add ? 'Tagged' : 'Removed tags from'} ${plural(sc.picked, 'item')}.`);
                refresh();
            },
        });
    };

    const reprocess = () => {
        setPopover(null);
        setConfirm({
            title: 'Rebuild thumbnails?',
            body: (
                <p>
                    Regenerates thumbnails for all {plural(all.length, 'item')} in this event. It runs in the background; the grid may show old thumbnails for a few
                    minutes.
                </p>
            ),
            actionLabel: 'Rebuild thumbnails',
            busyLabel: 'Starting…',
            run: async () => {
                try {
                    await authenticatedApi.post(`/activations/${id}/reprocessThumbnails`);
                } catch (e) {
                    return errText(e, 'Couldn’t start the rebuild — try again.');
                }
                toast.show('Rebuilding thumbnails in the background.');
            },
        });
    };

    const setAutoPref = (on: boolean) => {
        setAuto(on);
        try {
            window.localStorage.setItem(AUTO_KEY, on ? '1' : '0');
        } catch {
            /* storage blocked: session only */
        }
    };

    const setTagsLocal = (mediaId: number, tags: string[]) => setRow(mediaId, { tags });

    /* ---- render ---- */

    const head = (
        <>
            <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />
            <PageHeadActions
                title="Media"
                sub={
                    media == null
                        ? 'Loading…'
                        : `${plural(all.length, 'item')}${hiddenCount > 0 ? ` · ${hiddenCount.toLocaleString()} hidden` : ''}`
                }
                actions={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setSheet('upload')}>
                            <IconUpload />
                            Upload
                        </button>
                        <button type="button" className="tb-btn" disabled={media == null || filtered.length === 0} onClick={() => openZip(selected.size > 0 ? 'selection' : 'filtered')}>
                            <IconDownload />
                            Download
                        </button>
                        <button type="button" className="tb-btn" title="Bulk edit history" onClick={() => setSheet('history')}>
                            <IconHistory />
                            Edits
                        </button>
                        <button type="button" className="tb-btn ghost" onClick={() => navigate(`/activations/${id}/analyze`)}>
                            Analyze
                        </button>
                    </>
                }
            />
        </>
    );

    if (loadFailed) {
        return (
            <div className="ev-page med-page">
                {head}
                <TCard>
                    <TEmpty message="Couldn’t load this event’s media." actionLabel="Retry" onAction={() => load()} />
                </TCard>
                {toast.node}
            </div>
        );
    }

    const zipDone = zipRequests.filter((r: any) => r.zipRequestUri);
    const zipPending = zipRequests.length - zipDone.length;

    return (
        <div className="ev-page med-page">
            {head}

            {/* types · filter · view */}
            <div className="med-bar">
                <div className="med-types" role="group" aria-label="Media type">
                    {TYPES.filter((t) => t.key === 'all' || counts[t.key] > 0 || type === t.key).map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={type === t.key ? 'med-chip on' : 'med-chip'}
                            aria-pressed={type === t.key}
                            onClick={() => {
                                setType(t.key);
                                resetPage();
                            }}
                        >
                            {t.label}
                            <span className="n">{media == null ? '—' : (counts[t.key] ?? 0).toLocaleString()}</span>
                        </button>
                    ))}
                </div>
                <div className="med-tools">
                    <div ref={filterRef} className="med-pop">
                        <button
                            type="button"
                            className={filterCount > 0 ? 'tb-btn tb-on' : 'tb-btn tb-icon'}
                            aria-label="Filter by source, group or tag"
                            title="Filter"
                            aria-expanded={popover === 'filter'}
                            onClick={() => setPopover((p) => (p === 'filter' ? null : 'filter'))}
                        >
                            <IconFilter />
                            {filterCount > 0 && filterCount}
                        </button>
                        {popover === 'filter' && (
                            <div className="menu med-menu">
                                <MenuLabel>Source</MenuLabel>
                                <MenuItem on={source == null} onClick={pick(() => setSource(null))}>
                                    <span className="med-mi">All sources</span>
                                    <span className="med-mn">{all.length}</span>
                                </MenuItem>
                                {sources.map((s) => (
                                    <MenuItem key={s.key || ALL} on={source === s.key} onClick={pick(() => setSource(s.key))}>
                                        <span className="med-mi">{s.label}</span>
                                        <span className="med-mn">{s.count}</span>
                                    </MenuItem>
                                ))}
                                {groupKeys.length > 0 && (
                                    <>
                                        <MenuSep />
                                        <MenuLabel>Group</MenuLabel>
                                        <MenuItem on={group == null} onClick={pick(() => setGroup(null))}>
                                            <span className="med-mi">All groups</span>
                                        </MenuItem>
                                        {groupKeys.map(([k, n]) => (
                                            <MenuItem key={k} on={group === k} onClick={pick(() => setGroup(k))}>
                                                <span className="med-mi">{k}</span>
                                                <span className="med-mn">{n}</span>
                                            </MenuItem>
                                        ))}
                                    </>
                                )}
                                {tagCounts.length > 0 && (
                                    <>
                                        <MenuSep />
                                        <MenuLabel>Tag</MenuLabel>
                                        <MenuItem on={tag == null} onClick={pick(() => setTag(null))}>
                                            <span className="med-mi">All tags</span>
                                        </MenuItem>
                                        {tagCounts.map(([t, n]) => (
                                            <MenuItem key={t} on={tag === t} onClick={pick(() => setTag(t))}>
                                                <span className="med-mi">{t}</span>
                                                <span className="med-mn">{n}</span>
                                            </MenuItem>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div ref={viewRef} className="med-pop">
                        <button
                            type="button"
                            className="tb-btn tb-icon"
                            aria-label="View options"
                            title="View options"
                            aria-expanded={popover === 'view'}
                            onClick={() => setPopover((p) => (p === 'view' ? null : 'view'))}
                        >
                            <IconSliders />
                        </button>
                        {popover === 'view' && (
                            <div className="menu med-menu med-view">
                                <MenuLabel>Sort</MenuLabel>
                                <Segmented<'new' | 'old'>
                                    label="Sort"
                                    value={sortDesc ? 'new' : 'old'}
                                    onChange={(v) => setSortDesc(v === 'new')}
                                    options={[
                                        { value: 'new', label: 'Newest' },
                                        { value: 'old', label: 'Oldest' },
                                    ]}
                                />
                                <span className="med-cols">
                                    <MenuLabel>Columns</MenuLabel>
                                    <Segmented<string>
                                        label="Columns"
                                        value={String(cols)}
                                        onChange={(v) => setCols(Number(v))}
                                        options={['3', '4', '5', '6'].map((n) => ({ value: n, label: n }))}
                                    />
                                </span>
                                <MenuSep />
                                <div className="med-tg">
                                    <Toggle label="Show hidden" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                                </div>
                                <div className="med-tg">
                                    <Toggle label="Auto refresh" checked={auto} onChange={(e) => setAutoPref(e.target.checked)} />
                                </div>
                                <MenuSep />
                                <button type="button" className="menu-item" onClick={reprocess}>
                                    Rebuild thumbnails…
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Chipbar chips={chips} />

            {(zipRequests.length > 0 || newCount > 0) && (
                <div className="med-status">
                    {zipRequests.length > 0 && (
                        <button type="button" className="med-link" aria-expanded={zipListOpen} onClick={() => setZipListOpen((o) => !o)}>
                            Downloads:{' '}
                            {[zipPending > 0 && `${zipPending} in progress`, zipDone.length > 0 && `${zipDone.length} ready`].filter(Boolean).join(' · ')}{' '}
                            {zipListOpen ? '▴' : '▾'}
                        </button>
                    )}
                    {newCount > 0 && (
                        <button type="button" className="med-new" onClick={() => load(true)}>
                            <span className="dot" aria-hidden="true" />
                            {newCount} new — refresh
                        </button>
                    )}
                </div>
            )}
            {zipListOpen && zipRequests.length > 0 && (
                <TCard>
                    <ZipRequests rows={zipRequests} />
                </TCard>
            )}

            {faces.length > 0 && (
                <div className="med-faces">
                    <span className="field-l">People</span>
                    <span className="med-face-row">
                        {(facesExpanded ? faces : faces.slice(0, 10)).map((f: any) => (
                            <button
                                key={f.faceVector}
                                type="button"
                                className="med-face"
                                title="Open this person’s photos in the gallery"
                                aria-label="Open this person’s photos in the gallery"
                                disabled={!gallerySlug}
                                onClick={() =>
                                    window.open(
                                        `${galleryUrl}/face-query/${encodeURIComponent(gallerySlug ?? '')}?faceIds=${encodeURIComponent(f.faceVector)}`,
                                        '_blank',
                                        'noopener',
                                    )
                                }
                            >
                                <img src={f.faceCatalogUrl} alt="" loading="lazy" />
                            </button>
                        ))}
                        {faces.length > 10 && !facesExpanded && (
                            <button type="button" className="med-link" onClick={() => setFacesExpanded(true)}>
                                +{faces.length - 10} more
                            </button>
                        )}
                    </span>
                </div>
            )}

            {media == null ? (
                <div className="med-grid" style={{ '--med-cols': cols } as React.CSSProperties} aria-busy="true" aria-label="Loading">
                    {Array.from({ length: 12 }, (_, i) => (
                        <span key={i} className="med-skel" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <TCard>
                    {all.length === 0 ? (
                        <TEmpty message="No media yet — it lands here as devices capture." actionLabel="Upload" onAction={() => setSheet('upload')} />
                    ) : (
                        <TEmpty
                            message="No media matches these filters."
                            actionLabel="Clear filters"
                            onAction={() => {
                                setType('all');
                                setSource(null);
                                setGroup(null);
                                setTag(null);
                                setShowHidden(true);
                            }}
                        />
                    )}
                </TCard>
            ) : (
                <>
                    <div className={selected.size > 0 ? 'med-grid selecting' : 'med-grid'} style={{ '--med-cols': cols } as React.CSSProperties}>
                        {visible.map((m) => {
                            const on = selected.has(m.id);
                            const when = m.capturedAt ?? m.createdAt;
                            const busy = rowBusy.has(m.id);
                            const tags: string[] = m.tags ?? [];
                            return (
                                <div key={m.id} className={`med-tile${on ? ' on' : ''}${m.isHidden ? ' hid' : ''}`}>
                                    <button
                                        type="button"
                                        className="med-open"
                                        aria-label={selected.size > 0 ? `${on ? 'Deselect' : 'Select'} item` : 'Open in viewer'}
                                        onClick={(e) => (selected.size > 0 ? toggleSelect(m, e.shiftKey) : openLightbox(m))}
                                    >
                                        <img src={m.thumbnailUri ?? m.thumbnailTinyUri ?? m.originalUri} alt="" loading="lazy" />
                                    </button>
                                    <button
                                        type="button"
                                        role="checkbox"
                                        aria-checked={on}
                                        aria-label="Select item"
                                        className="med-check"
                                        onClick={(e) => toggleSelect(m, e.shiftKey)}
                                    >
                                        <IconCheck />
                                    </button>
                                    <span className="med-badges">
                                        {m.isFavorite && (
                                            <span className="med-b" title="Favorite">
                                                <IconHeart filled />
                                            </span>
                                        )}
                                        {m.isHidden && <span className="med-b txt">Hidden</span>}
                                    </span>
                                    {isVideo(m) && (
                                        <span className="med-play" aria-label="Video">
                                            <IconPlay />
                                        </span>
                                    )}
                                    <span className="med-acts">
                                        <button
                                            type="button"
                                            aria-label={m.isFavorite ? 'Unfavorite' : 'Favorite'}
                                            title={m.isFavorite ? 'Unfavorite' : 'Favorite'}
                                            aria-pressed={!!m.isFavorite}
                                            disabled={busy}
                                            onClick={() => toggleFavorite(m)}
                                        >
                                            <IconHeart filled={!!m.isFavorite} />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={m.isHidden ? 'Show in gallery' : 'Hide from gallery'}
                                            title={m.isHidden ? 'Show' : 'Hide'}
                                            disabled={busy}
                                            onClick={() => toggleHidden(m)}
                                        >
                                            {m.isHidden ? <IconEye /> : <IconEyeOff />}
                                        </button>
                                    </span>
                                    {tags.length > 0 && (
                                        <span className="med-tags">
                                            {tags.slice(0, 2).map((t) => (
                                                <span key={t}>{t}</span>
                                            ))}
                                            {tags.length > 2 && <span>+{tags.length - 2}</span>}
                                        </span>
                                    )}
                                    <span className="med-meta ev-mono">
                                        {deviceName(m.deviceKeyId ?? null)}
                                        {when && ` · ${moment(when).format('h:mm A')}`}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="ev-foot med-foot">
                        <span className="quiet-empty">
                            {visible.length.toLocaleString()} of {filtered.length.toLocaleString()} · {sortDesc ? 'newest' : 'oldest'} first
                        </span>
                        {filtered.length > limit && (
                            <button type="button" className="tb-btn" onClick={() => setLimit((l) => l + PAGE)}>
                                Show more
                            </button>
                        )}
                    </div>
                </>
            )}

            {selected.size > 0 && (
                <div className="savebar med-selbar" role="region" aria-label="Selection actions">
                    <span className="msg">
                        <b>{selected.size.toLocaleString()} selected</b>
                    </span>
                    <span className="sp" />
                    <span className="med-selacts">
                        <button type="button" className="tb-btn sm" onClick={() => bulkHide(!anySelectedHidden)}>
                            {anySelectedHidden ? 'Show' : 'Hide'}
                        </button>
                        <button type="button" className="tb-btn sm" onClick={downloadSelection}>
                            Download
                        </button>
                        <button type="button" className="tb-btn sm" onClick={() => setSheet('edit')}>
                            Edit…
                        </button>
                        <button type="button" className="tb-btn sm" onClick={() => setSheet('tag')}>
                            Tag…
                        </button>
                        <button type="button" className="tb-btn sm" onClick={() => setSheet('share')}>
                            Share…
                        </button>
                        <button type="button" className="tb-btn sm" onClick={openCopy}>
                            Copy to…
                        </button>
                        <button type="button" className="tb-btn ghost sm" onClick={clearSelection}>
                            Clear
                        </button>
                    </span>
                </div>
            )}

            {lb && (
                <Lightbox
                    ids={lb.ids}
                    byId={byId}
                    id={lb.id}
                    onStep={stepLightbox}
                    onClose={closeLightbox}
                    deviceName={deviceName}
                    onFavorite={toggleFavorite}
                    onHidden={toggleHidden}
                    onReplace={replaceFile}
                    onHistory={setVersionsOf}
                    onTags={setTagsLocal}
                    onError={toast.show}
                    busy={rowBusy}
                />
            )}

            {sheet === 'zip' && <ZipSheet scope={zipScope} label={zipLabel} onClose={() => setSheet(null)} onReview={reviewZip} />}
            {sheet === 'share' && <ShareSheet scope={selScope} galleryReady={galleryId != null} onClose={() => setSheet(null)} onReview={reviewShare} />}
            {sheet === 'copy' && (
                <CopySheet scope={selScope} targets={copyTargets} failed={copyFailed} onRetry={loadTargets} onClose={() => setSheet(null)} onPick={reviewCopy} />
            )}
            {sheet === 'tag' && (
                <TagSheet
                    scope={selScope}
                    activationTags={tagCounts.map(([t]) => t)}
                    selectionTags={selectionTags}
                    onClose={() => setSheet(null)}
                    onReview={reviewTags}
                />
            )}
            {sheet === 'edit' && (
                <BulkEditSheet
                    activationId={id}
                    galleryId={galleryId}
                    media={selectedRows}
                    confirm={setConfirm}
                    onClose={() => setSheet(null)}
                    onStarted={(batch) => {
                        setSheet(null);
                        clearSelection();
                        setActiveBatch(batch);
                    }}
                />
            )}
            {sheet === 'history' && <EditHistorySheet activationId={id} confirm={setConfirm} onClose={() => setSheet(null)} onChanged={refresh} />}
            {activeBatch && (
                <EditProgressSheet
                    batch={activeBatch}
                    confirm={setConfirm}
                    onClose={() => {
                        setActiveBatch(null);
                        refresh();
                    }}
                    onChanged={refresh}
                />
            )}
            {versionsOf && <VersionsSheet media={versionsOf} confirm={setConfirm} onClose={() => setVersionsOf(null)} onChanged={refresh} />}
            {sheet === 'upload' && (
                /* BulkUploadMedia brings its own modal; the wrapper only bridges tokens */
                <div className="psv3-legacy">
                    <BulkUploadMedia
                        activationId={id}
                        galleryId={galleryId}
                        onCancel={() => setSheet(null)}
                        onComplete={() => {
                            setSheet(null);
                            refresh();
                        }}
                    />
                </div>
            )}

            <ConfirmHost spec={confirm} onClose={() => setConfirm(null)} />
            {toast.node}
        </div>
    );
}

export default function MediaV3() {
    return (
        <EventPage section="media">
            <MediaBody />
        </EventPage>
    );
}
